import {
  ContentQualityQuestionData,
  ContentQualityResponseOptions,
} from '@amzn/awscat-pattern-library-service-model';
import React, { useEffect, useState, type JSX } from 'react';
import {
  Box,
  FormField,
  Grid,
  InputProps,
  Link,
  NonCancelableCustomEvent,
  Select,
  SelectProps,
  SpaceBetween,
  Textarea,
} from '@amzn/awsui-components-core';
import { getResponseOptions } from '../utils/SelectOptions';
import { getDefaultFilterSelection } from '../../api/UtilsApi';
import { responseOptionsDisplayNames } from '../utils/DisplayNames';
import arraySort from 'array-sort';
import { ACSII_VALUE_FOR_A } from '../utils/ContentQualityUtils';
import { COMMENT_MAX } from '../utils/ContentQualityUtils';

interface Props {
  question: ContentQualityQuestionData;
  response: string;
  comment: string;
  shouldDisplay?: boolean;
  handleResponseUpdated?: (
    sequence: number,
    response: ContentQualityResponseOptions,
    comment: string,
  ) => void;
  readonly?: boolean;
}

interface IndexedLink {
  word: string;
  link: string;
  index: number;
}

const QuestionnaireQuestionItem: React.FunctionComponent<Props> = (props: Props) => {
  const {
    question,
    response: questionResponse,
    comment: responseComment,
    shouldDisplay,
    handleResponseUpdated,
    readonly,
  } = props;
  const { responseOptions, questionSequence, questionBody, note, hyperlinks } = question;

  const responsesToExclude = Object.keys(ContentQualityResponseOptions).filter(
    (op) => !responseOptions.has(op as ContentQualityResponseOptions),
  );

  /* States */
  const [response, setResponse] = useState<string>(questionResponse);
  const [comment, setComment] = useState<string>(responseComment);
  const [showCommentBox, setShowCommentBox] = useState<boolean>(
    questionResponse === ContentQualityResponseOptions.NO,
  );

  useEffect(() => {
    setResponse(questionResponse);
    setComment(responseComment);
    setShowCommentBox(questionResponse === ContentQualityResponseOptions.NO);
  }, [questionResponse, responseComment]);

  const onResponseSelectionUpdated = (
    event: NonCancelableCustomEvent<SelectProps.ChangeDetail>,
  ) => {
    const updatedResponse = event.detail.selectedOption.value as ContentQualityResponseOptions;
    let updatedComment = comment;
    if (updatedResponse !== ContentQualityResponseOptions.NO) {
      updatedComment = '';
      setComment(updatedComment);
      setShowCommentBox(false);
    }
    setResponse(updatedResponse);
    if (handleResponseUpdated) {
      handleResponseUpdated(Number(questionSequence), updatedResponse, updatedComment);
    }
  };

  const onCommentUpdated = (event: NonCancelableCustomEvent<InputProps.ChangeDetail>) => {
    const updatedComment = event.detail.value as string;
    setComment(updatedComment);
    if (handleResponseUpdated) {
      handleResponseUpdated(
        Number(questionSequence),
        response as ContentQualityResponseOptions,
        updatedComment,
      );
    }
  };

  const getQuestionSequenceToDisplay = () => {
    if (questionSequence.includes('.')) {
      // if question is a sub-question, display a, b, c, d... as the sequence
      // x.1 -> a; x.2 -> b; etc.
      const sequenceArr = questionSequence.split('.');
      return `${String.fromCharCode(ACSII_VALUE_FOR_A + Number(sequenceArr[1]) - 1)}. `;
    } else {
      return `${questionSequence}. `;
    }
  };

  const getQuestionBody = () => {
    if (!hyperlinks) {
      return questionBody;
    } else {
      const questionElements: JSX.Element[] = [];

      // sort hyperlinks based on the text index in question body
      const linksWithIndex: IndexedLink[] = hyperlinks.links.map((hyperlink) => {
        const { link, word } = hyperlink;
        const index = questionBody.indexOf(word);
        return { word, link, index };
      });
      arraySort(linksWithIndex, 'index');

      // construct question elements
      let startIndex = 0;
      let key = 0;
      linksWithIndex.forEach((hl) => {
        const { word, link, index } = hl;
        const textBeforeLink = questionBody.substring(startIndex, index);
        questionElements.push(<span key={key++}>{textBeforeLink}</span>);
        questionElements.push(
          <Link key={key++} external href={link}>
            {word}
          </Link>,
        );
        startIndex = index + word.length;
      });

      // append string after last link
      questionElements.push(<span key={key}>{questionBody.substring(startIndex)}</span>);

      return questionElements;
    }
  };

  const questionBoxMargin = !isNaN(+getQuestionSequenceToDisplay()) ? 'xxs' : 'l'; // indent if sub-question
  const noteLine = note ? <Box variant="small">{note}</Box> : null;

  const selectedResponse = response
    ? getDefaultFilterSelection(
        response,
        responseOptionsDisplayNames.get(response as ContentQualityResponseOptions) || response,
        response,
      )
    : null;

  const readonlyItem = (
    <Grid gridDefinition={[{ colspan: 10 }, { colspan: 2 }]}>
      <Box margin={{ bottom: 'xs', left: questionBoxMargin }}>
        <SpaceBetween direction="vertical" size="xxxs">
          <div>
            {getQuestionSequenceToDisplay()}
            {getQuestionBody()}
          </div>
          <p>
            <em>{comment}</em>
          </p>
        </SpaceBetween>
      </Box>
      <Box>{responseOptionsDisplayNames.get(response as ContentQualityResponseOptions)}</Box>
    </Grid>
  );

  const renderCommentMessage = () => {
    const charactersRemaining = COMMENT_MAX - comment.length;

    return (
      <FormField
        errorText={comment.length > COMMENT_MAX && `Must be less than ${COMMENT_MAX} characters`}
        constraintText={
          comment.length > COMMENT_MAX ? (
            <></>
          ) : (
            <Box color="text-body-secondary">{`${charactersRemaining} character${charactersRemaining > 1 ? 's' : ''} remaining`}</Box>
          )
        }
        i18nStrings={{ errorIconAriaLabel: 'Error' }}
      >
        <Textarea
          value={comment}
          onChange={onCommentUpdated}
          placeholder="Enter comments here"
          rows={3}
          ariaRequired
        />
      </FormField>
    );
  };

  const commentBox = showCommentBox ? <div>{renderCommentMessage()}</div> : null;

  const editableFormItem = (
    <Grid gridDefinition={[{ colspan: 10 }, { colspan: 2 }]}>
      <FormField i18nStrings={{ errorIconAriaLabel: 'Error' }}>
        <Box margin={{ left: questionBoxMargin }}>
          <SpaceBetween direction="vertical" size="s">
            <div>
              <div>
                {getQuestionSequenceToDisplay()}
                {getQuestionBody()}
              </div>
              {noteLine}
            </div>
            {commentBox}
          </SpaceBetween>
        </Box>
      </FormField>
      <Box margin={{ top: 'xs' }}>
        <Select
          selectedOption={selectedResponse}
          options={getResponseOptions(responsesToExclude)}
          onChange={onResponseSelectionUpdated}
          ariaRequired
        />
      </Box>
    </Grid>
  );

  return shouldDisplay ? (!!readonly ? readonlyItem : editableFormItem) : null;
};

export default QuestionnaireQuestionItem;
